/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {getAccountId} from '@ion/applications/src/helpers';
import {error} from '@ion/core/src/logger/logger';
import {
  loadAllAEMPConfigs,
  loadAllDeviceTypes
} from '@ion/core/src/services/Connect';
import {CTCEntity} from '@ion/core/src/types/entities';
import {downloadBlankTemplate} from '@ion/core/src/xlsx/TemplateHelpers';
import {
  ExportClient,
  exportCSV,
  ExportXLSType,
  generateRows,
  paging
} from '@ion/dls/src/grid/Exporter';
import {dehydrateCurrentUser} from '@ion/query/src/hooks/useCurrentUser';
import {ExportContext} from './../../types/redux';

/**
 * defines the shape of the report export paramaters which are generally used for
 * export the report grid.
 */
export interface ExportReport extends ExportClient {
  items: CTCEntity[];
  title?: string;
  subTitle?: string;
  augmenterArgs?: any[];
}

// invoked when an export is initiated ...
export const EXPORT_REQUEST   = 'EXPORT_REQUEST';

// invoked when an export has completed ...
export const EXPORT_RESPONSE  = 'EXPORT_RESPONSE';

// invoked when an export has failed ...
export const EXPORT_ERROR     = 'EXPORT_ERROR';

// invoked when the user has been notified of an initiated export ...
export const EXPORT_CONFIRMED = 'EXPORT_CONFIRMED';

// invoked when the user has been notified of a completed export ...
export const EXPORT_COMPLETED = 'EXPORT_COMPLETED';

/**
 * General utility which helps in downloading templates as excel with pre-populated
 * values specific to current CTC Entity type.
 */
export const exportXLS = ({
  entity,
  items,
  columns,
  augmenter,
  augmenterArgs,
  params
}: ExportXLSType) => {
  const rows: CTCEntity[] = generateRows({
    entity,
    items,
    columns,
    augmenter,
    augmenterArgs
  });
  params.rows = rows;
  downloadBlankTemplate({...params});
};

/**
 * Provides the AEMPConfig Export Action Creator which manages retrieving all aempconfigs
 * and converting the retrieved aempconfigs to a CSV for subsequent download.
 */
export const exportAempConfig = ({columns, augmenter, items, ...rest}: ExportClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.aempConfig;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      const accountId = getAccountId(getState);
      const user      = dehydrateCurrentUser();
      const configs   = (
        !items?.length
          ? await loadAllAEMPConfigs({accountId, ...rest, ...paging})
          : items
      );
      exportCSV({
        augmenterArgs    : [true],
        includeTimestamp : true,
        items            : configs,
        entity,
        columns,
        augmenter
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Asset Export Action Creator which manages retrieving all assets
 * and converting the retrieved assets to a CSV for subsequent download.
 */
export const exportDeviceTypes = ({columns, augmenter, items, ...rest}: ExportClient) => {
  return async(dispatch: Function): Promise<any> => {
    const entity = ExportContext.deviceTypes;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      const deviceTypes = (
        !items?.length
          ? await loadAllDeviceTypes({...rest, ...paging})
          : items
      );
      exportCSV({
        includeTimestamp : true,
        items            : deviceTypes,
        entity,
        columns,
        augmenter
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Invoked when an export has completed and the user confirms acknowledgement.
 */
export const exportConfirmed = () => ({
  type : EXPORT_CONFIRMED
});

/**
 * Invoked when an export has completed and the user confirms acknowledgement.
 */
export const exportComplete = () => ({
  type : EXPORT_COMPLETED
});
